import { Bookmark, Plus, Search } from "lucide-react";
import { BaseActionProps } from "@/components/common/adCard/types";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { useRef, useState } from "react";
import { useCreativeLibraryStore } from "../../store";
import { createFolder } from "../../apiService";
import { useParams } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Folder } from "../../types";
import { Button } from "@/components/ui/button";
import { IconSquareRoundedMinus } from "@tabler/icons-react";

interface BookmarkActionProps extends BaseActionProps {
  folderId?: string | null;
  onBookmark?: (folderId: string | null) => void;
}

const BookmarkAction = ({ folderId, onBookmark }: BookmarkActionProps) => {
  const { accountId } = useParams();
  const { folders, setFolders } = useCreativeLibraryStore();
  const [folderSearchQuery, setFolderSearchQuery] = useState<string>("");
  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false);
  const createFolderInputRef = useRef<HTMLInputElement>(null);
  const folderListRef = useRef<HTMLDivElement>(null);
  const folderSearchInputRef = useRef<HTMLInputElement>(null);

  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearchQuery.toLowerCase()),
  );

  const handleCreateFolder = async () => {
    if (!accountId) return;
    try {
      const folderName = createFolderInputRef.current?.value.trim();
      // Check if folder name already exists
      const nameExists = folders.some((folder: Folder) => folder.name === folderName);
      if (nameExists) {
        // Could add toast notification here
        console.error("A folder with this name already exists");
        return;
      }
      if (folderName) {
        const res = await createFolder(accountId, folderName);
        setFolders([...folders, res.data]);
        // Scroll to bottom after folder is created
        if (folderListRef.current) {
          requestAnimationFrame(() => {
            folderListRef.current?.scrollTo({
              top: folderListRef.current.scrollHeight,
              behavior: "smooth",
            });
          });
        }
      }
      setIsCreatingFolder(false);
    } catch (error) {
      console.error("Error creating folder:", error);
      setIsCreatingFolder(false);
    }
  };

  const handleRemoveBookmark = () => onBookmark?.(null);
  const handleOnFolderClick = (folder: Folder) => {
    if (folderId) {
      // already bookmarked
      onBookmark?.(folder.id);
      toast.message(
        <ToastContent
          headingPrefix="Creative moved to "
          headingHighlight={`"${folder.name}"`}
          subheadingPrefix="Successfully switched folder of creative."
        />,
      );
    } else {
      // new bookmark
      onBookmark?.(folder.id);
    }
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) setIsCreatingFolder(false); //resetting create folder state
      }}
    >
      <DropdownMenuTrigger asChild>
        <button className="pointer-events-auto bg-[#00000099] backdrop-blur-sm hover:bg-[#000000CC] font-['Helvetica_Neue'] text-[12px] font-normal leading-[16px] text-[#fff] p-[6px] rounded-[8px] cursor-pointer focus:outline-none focus:ring-0">
          <Bookmark size={16} color="#fff" fill={folderId ? "#fff" : "none"} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        className="rounded-[12px] border-[0.8px] border-border-gray shadow-sm p-0 w-[185px]"
      >
        {folderId && (
          <div className="w-full p-1">
            <Button
              className="border-0 bg-[#D92D2014] hover:bg-[#D92D2024] justify-start w-full shadow-none h-8 px-2"
              onClick={handleRemoveBookmark}
            >
              <IconSquareRoundedMinus className="text-destructive-red" size={14} />
              <span className="text-destructive-red font-normal not-italic text-[13px] leading-[20px] tracking-[0%] relative top-[1px]">
                Remove
              </span>
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2 px-3 py-2">
          <Search size={14} strokeWidth={1.5} color="#021859" className="shrink-0" />
          <input
            ref={folderSearchInputRef}
            type="text"
            placeholder="Search"
            value={folderSearchQuery}
            onChange={(e) => {
              setFolderSearchQuery(e.target.value);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-transparent outline-none placeholder:text-brand-primary-400 text-brand-primary-new font-['Helvetica_Neue'] text-[13px] font-normal leading-[20px]"
          />
        </div>
        <Separator className="ml-1 w-[96%]" />
        <div ref={folderListRef} className="p-1 max-h-[240px] overflow-y-auto">
          {filteredFolders.map((folder) => (
            <DropdownMenuItem
              key={folder.id}
              className={`mb-1 focus:bg-transparent hover:bg-hover-gray focus:text-inherit ${folder.id === folderId ? "bg-selected-gray cursor-default pointer-events-none" : "cursor-pointer"}`}
              onClick={() => handleOnFolderClick(folder)}
            >
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {folder.name}
              </span>
            </DropdownMenuItem>
          ))}
        </div>
        <Separator className="ml-1 w-[96%] text-[#E4E4E7]" />
        <div className="px-1 py-0 h-9">
          {!isCreatingFolder ? (
            <button
              className="flex items-center gap-2 w-full cursor-pointer rounded-[4px] h-8 px-2 py-[6px]"
              onClick={() => setIsCreatingFolder(true)}
            >
              <Plus size={16} color="#FF4500" />
              <span className="text-[13px] font-normal leading-[20px] text-[#FF4500] relative top-[1px]">
                Create folder
              </span>
            </button>
          ) : (
            <Input
              ref={createFolderInputRef}
              placeholder="Folder name"
              className="w-full h-8 p-2 border-0 rounded-[4px] text-[14px] focus:outline-none shadow-none leading-none"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  void handleCreateFolder();
                } else if (e.key === "Escape") {
                  setIsCreatingFolder(false);
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onBlur={() => setIsCreatingFolder(false)}
            />
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BookmarkAction;
